import { useEffect, useState } from "react";

/* Three states, because the tokens have three.
 *
 * `tokens.css` defines light on bare `:root`, dark under BOTH a `prefers-color-scheme` query and
 * a `[data-theme="dark"]` attribute. That shape exists so an explicit choice can win in either
 * direction while the default — no attribute at all — still follows the operating system. A
 * two-state toggle cannot express "follow the system" once it has been touched, so this is a
 * radio group of three rather than a switch.
 *
 * A `fieldset`/`legend` and real radios, not buttons: the browser then gives arrow-key movement
 * between the options and announces "2 of 3" for free. Rebuilding that with `div`s and click
 * handlers is how a control ends up keyboard-hostile.
 */

import { themeFromStored, type Theme } from "../lib/theme";
import { readPref, writePref } from "../lib/viewPrefs";
import { Icon } from "./Icon";

function apply(theme: Theme) {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

function stored(): Theme {
  try {
    // W140: the signed-in coach's own choice (`viewPrefs`), never another coach's on the same browser.
    return themeFromStored(readPref("theme"));
  } catch {
    // A private window, or site data blocked. The accessor itself can throw, so the read is
    // wrapped rather than merely null-checked, and the page renders correctly with no value.
    // W72 step 7: with nothing readable the default is the SYSTEM's theme (`lib/theme.ts`), never a forced dark.
    return themeFromStored(null);
  }
}

export function ThemeControl() {
  const [theme, setTheme] = useState<Theme>(stored);

  useEffect(() => {
    apply(theme);
    // Per-coach convenience only (`writePref` never throws). Failing to remember the choice is not failing.
    writePref("theme", theme);
  }, [theme]);

  /* W81 (the pro web step 1, his note 2026-09-25 ~20:55): *"light, dark and auto"*, in that order, as a `.seg` directly
     above the coach's card. Still three REAL radios in a fieldset — arrow keys and "2 of 3" are the browser's — and
     rendered ONCE, by `Shell` (gate `w81-1-the-tokens-and-the-shell` counts the call sites). */
  return (
    <fieldset className="theme-control seg">
      <legend className="ht-visually-hidden">Appearance</legend>
      {(["light", "dark", "system"] as const).map((option) => (
        <label key={option} className="theme-option seg-option">
          <input
            type="radio"
            name="theme"
            value={option}
            checked={theme === option}
            onChange={() => setTheme(option)}
          />
          {option === "light" && <Icon name="sun" />}
          {option === "dark" && <Icon name="moon" />}
          <span>{option === "system" ? "Auto" : option === "light" ? "Light" : "Dark"}</span>
        </label>
      ))}
    </fieldset>
  );
}
